namespace Solovis.Reports.Parts.Tables.DataTableHelpers.TableModel {
    /*
     * TableModel contains helpers for working with the table model rows and columns.
     */

    export function flattenVisibleRows(sourceRows, flattenOptions) {
        /// <summary>
        /// flattenVisibleRows will flatten the visible rows into containers that have the row (or group row) and level depth,
        /// so that we can easily see the complete list of visible rows (and in the future, support virtual scrolling).
        /// sourceRows will be hierarchical in nature, where groups have references to child rows.
        /// flattenOptions can have { aggregateRowDisplay: "inline/top/bottom", isExpandedCallback: function(nestedGroupKey) { returns true/false },
        ///   keepGroupSubRowsOnCollapse: true/false }
        /// </summary>

        var rowList = [];

        var aggregateDisplay = flattenOptions.aggregateRowDisplay ? flattenOptions.aggregateRowDisplay : "top";
        var isExpandedCallback = flattenOptions.isExpandedCallback ? flattenOptions.isExpandedCallback : function () { return true };
        const keepGroupSubRowsOnCollapse = flattenOptions.keepGroupSubRowsOnCollapse ? true : false;

        if (flattenOptions.tableSettingsToUse && flattenOptions.tableSettingsToUse.aggregateRowDisplay) {
            aggregateDisplay = flattenOptions.tableSettingsToUse.aggregateRowDisplay;
        }

        TableModel._flattenVisibleRowsHelper(sourceRows, rowList, 0, null, aggregateDisplay, isExpandedCallback, keepGroupSubRowsOnCollapse);

        return rowList;
    };

    export function _flattenVisibleRowsHelper(sourceRowList, targetRowList, level, parentRowData, aggregateDisplay, isExpandedCallback, keepGroupSubRowsOnCollapse?) {
        if (sourceRowList) {
            $(sourceRowList).each(function (rowIndex, row: any) {
                var aggregateDisplayToUse = aggregateDisplay;
                if (row._subRowReplacement) {
                    // We always use inline aggregates when processing a row created by subrow processing.
                    aggregateDisplayToUse = "inline";
                }

                if (row._groupId) {
                    var rowData: any = {
                        parentRow: parentRowData,
                        row: row,
                        level: level,
                        inlineAggregates: aggregateDisplayToUse == "inline",
                        isGroupHeader: true
                    };

                    var nestedGroupKey = row._nestedGroupKey;
                    rowData.nestedGroupKey = nestedGroupKey;

                    targetRowList.push(rowData);

                    var aggregateRowData = null;
                    if (aggregateDisplayToUse == "top" || aggregateDisplayToUse == "bottom") {
                        aggregateRowData = {
                            parentRow: parentRowData,
                            row: row,
                            level: level,
                            isAggregateRow: true
                        };
                    }

                    if (aggregateDisplayToUse == "top" && (row._groupTotals || row._backingRow)) {
                        targetRowList.push(aggregateRowData);
                    }

                    // The grouping line's own sub-rows (e.g. its benchmark/excess-return rows) stay
                    // visible even when the group is collapsed. They live on _backingSubRows rather
                    // than _rows so that only the group's detail rows are gated behind expansion.
                    if (keepGroupSubRowsOnCollapse && row._backingSubRows) {
                        TableModel._flattenVisibleRowsHelper(row._backingSubRows, targetRowList, level + 1, rowData, aggregateDisplayToUse, isExpandedCallback, keepGroupSubRowsOnCollapse);
                    }

                    // Flatten child rows.
                    if (row._rows) {
                        if (isExpandedCallback(nestedGroupKey)) {
                            rowData.expanded = true;

                            TableModel._flattenVisibleRowsHelper(row._rows, targetRowList, level + 1, rowData, aggregateDisplayToUse, isExpandedCallback, keepGroupSubRowsOnCollapse);
                        }
                        else {
                            rowData.expanded = false;
                        }
                    }

                    if (aggregateDisplayToUse == "bottom" && (row._groupTotals || row._backingRow)) {
                        targetRowList.push(aggregateRowData);
                    }
                }
                else {
                    var rowData: any = {
                        parentRow: parentRowData,
                        row: row,
                        level: level
                    };

                    targetRowList.push(rowData);
                }
            });
        }
    };


    export interface SubRowFactoryRow {
        [key: string]: any;
        _rows?: SubRowFactoryRow[];
        _backingRow?: SubRowFactoryRow;
        _backingSubRows?: SubRowFactoryRow[];
        _groupId?: any;
        _groupKey?: any;
        _nestedGroupKey?: string;
        _subRowReplacement?: boolean;
    }

    export function processSubRowFactory(
        tableControl: Solovis.Reports.Parts.Tables.DataTableControl,
        processedRows: SubRowFactoryRow[],
        subRowFactory: (row: SubRowFactoryRow) => SubRowFactoryRow[] | null,
        subRowGroupKeyGetter: (row: SubRowFactoryRow) => string,
        keepGroupSubRowsOnCollapse?: boolean,
        removeClaimedNestedRows?: boolean
    ): SubRowFactoryRow[] {
        // removeClaimedNestedRows is for reflexive hierarchies where a sub-row is also its own
        // top-level entry in processedRows (e.g. managers). Without it, that row still renders
        // a second time at its own top-level slot. Existing callers are unaffected.
        const claimedNestedRows = removeClaimedNestedRows ? new Set<SubRowFactoryRow>() : null;

        TableModel._processSubRowFactoryForLevel(tableControl, processedRows, subRowFactory, subRowGroupKeyGetter, 0, null, keepGroupSubRowsOnCollapse, claimedNestedRows);

        if (!claimedNestedRows) {
            return processedRows;
        }

        return processedRows.filter((row) => {
            const identity = row && row._backingRow ? row._backingRow : row;
            return !claimedNestedRows.has(identity);
        });
    };

    // Returns a new ancestor set extended with row, without mutating the caller's set - each
    // recursive branch needs its own copy of the path from the root down to it.
    function extendAncestorRows(row: SubRowFactoryRow, ancestorRows: Set<SubRowFactoryRow> = new Set()): Set<SubRowFactoryRow> {
        const next = new Set(ancestorRows);
        next.add(row);
        return next;
    }

    // Rows already warned about per table instance, so a persistent data problem doesn't pop up
    // a dialog again on every re-render (sort/filter/expand all call this). WeakMap so entries are
    // dropped once a table instance is gone, rather than living for the lifetime of the module.
    const reportedCircularReferenceRows = new WeakMap<Solovis.Reports.Parts.Tables.DataTableControl, Set<SubRowFactoryRow>>();

    // Describes row using the table's first visible column - by convention the primary
    // identifying one (Name, Label, etc.) - since this helper is generic and has no other way
    // to know which of the report's own fields would mean something to the user.
    function describeRow(tableControl: Solovis.Reports.Parts.Tables.DataTableControl, row: SubRowFactoryRow): string {
        const primaryColumn = tableControl.columnSet.getVisibleColumnDefinitions()[0];
        if (!primaryColumn) {
            return "";
        }

        const value = tableControl._getColumnValueForRow(primaryColumn, row);
        return String(value ?? "");
    }

    function reportCircularReference(tableControl: Solovis.Reports.Parts.Tables.DataTableControl, row: SubRowFactoryRow): void {
        let reportedRows = reportedCircularReferenceRows.get(tableControl);
        if (!reportedRows) {
            reportedRows = new Set<SubRowFactoryRow>();
            reportedCircularReferenceRows.set(tableControl, reportedRows);
        }

        if (reportedRows.has(row)) {
            return;
        }
        reportedRows.add(row);

        const rowLabel = describeRow(tableControl, row) || "the underlying data";
        const message = `This list could not fully render its hierarchy because "${rowLabel}" has a circular parent reference. Some rows may be missing or incomplete.`

        new Solovis.UI.Dialog({
            container: $("<div>").text(message),
            modal: true,
            autoOpen: true,
            title: "Circular Reference Detected",
            center: true,
            width: 500,
            height: 200,
            maxWidth: 750,
            maxHeight: 400,
            destroyOnClose: true,
            actions: ["Close"]
        });
    }

    export function _processSubRowFactoryForLevel(
        tableControl: Solovis.Reports.Parts.Tables.DataTableControl,
        processedRows: SubRowFactoryRow[] | undefined,
        subRowFactory: (row: SubRowFactoryRow) => SubRowFactoryRow[] | null,
        subRowGroupKeyGetter: (row: SubRowFactoryRow) => string,
        level: number,
        parentNestedGroupKey: string | null,
        keepGroupSubRowsOnCollapse?: boolean,
        claimedNestedRows?: Set<SubRowFactoryRow> | null,
        ancestorRows: Set<SubRowFactoryRow> = new Set()
    ): void {
        processedRows?.forEach(function (row, i) {
            if (row._rows) {
                // Processing sub rows for group row.
                if (row._backingRow) {
                    const backingSubRows = subRowFactory.call(tableControl, row._backingRow);
                    if (!_.isEmpty(backingSubRows)) {
                        backingSubRows?.forEach(function (subRow) {
                            subRow._subRowReplacement = true;
                            if (claimedNestedRows) {
                                claimedNestedRows.add(subRow);
                            }
                        });
                        const replacementRow: SubRowFactoryRow = { 
                          _backingRow: row._backingRow,
                          _groupKey: row._groupKey,
                          _nestedGroupKey: row._nestedGroupKey,
                          _groupId: row._groupId,
                          _backingSubRows: undefined,
                          _rows: undefined,
                        };
                        if (keepGroupSubRowsOnCollapse) {
                            // Keep the grouping line's own sub-rows separate from its detail rows so
                            // they can stay visible when the group is collapsed (see _flattenVisibleRowsHelper).
                            replacementRow._backingSubRows = backingSubRows ?? undefined;
                            replacementRow._rows = row._rows;
                        }
                        else {
                            replacementRow._rows = backingSubRows?.concat(row._rows);
                        }

                        // Place in original slot in processedRows.
                        processedRows[i] = replacementRow;
                    }
                }
                const nextAncestorRows = row._backingRow ? extendAncestorRows(row._backingRow, ancestorRows) : ancestorRows;
                TableModel._processSubRowFactoryForLevel(tableControl, row._rows, subRowFactory, subRowGroupKeyGetter, level + 1, row._nestedGroupKey, keepGroupSubRowsOnCollapse, claimedNestedRows, nextAncestorRows);
            }
            else {
                if (ancestorRows.has(row)) {
                    // row is already its own ancestor along this path - the underlying Parent
                    // Manager data has a cycle. Stop descending here instead of recursing forever.
                    reportCircularReference(tableControl, row);
                    return;
                }

                const subRows = subRowFactory.call(tableControl, row);
                if (subRows && subRows.length > 0) {
                  const groupKey = subRowGroupKeyGetter(row);
                  const nestedGroupKey = parentNestedGroupKey ? parentNestedGroupKey + "||" + groupKey : groupKey;

                  if (claimedNestedRows) {
                    subRows?.forEach((subRow: SubRowFactoryRow) => claimedNestedRows.add(subRow));
                  }

                  const replacementRow: SubRowFactoryRow = {
                    _subRowReplacement: true, _backingRow: row,
                    _groupKey: groupKey,
                    _nestedGroupKey: nestedGroupKey,
                    _groupId: "subRowFactory",
                    _rows: subRows ?? undefined,
                  }

                    // Place in original slot in processedRows.
                    processedRows[i] = replacementRow;

                    // Recurse so subRowFactory can nest a grandchild too (3+ tier hierarchies).
                    TableModel._processSubRowFactoryForLevel(tableControl, replacementRow._rows, subRowFactory, subRowGroupKeyGetter, level + 1, nestedGroupKey, keepGroupSubRowsOnCollapse, claimedNestedRows, extendAncestorRows(row, ancestorRows));
                }
            }
        });
    };
}
